// src/utils/priceHelpers.js

/**
 * Utility helpers for unified price and currency handling.
 */

export const SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", AUD: "$", CAD: "$",
  JPY: "¥", CNY: "¥", KRW: "₩", INR: "₹", RUB: "₽",
  SEK: "kr", DKK: "kr", NOK: "kr", CHF: "Fr.",
  PLN: "zł", CZK: "Kč", BGN: "Лв.", HRK: "kn",
  TRY: "₺", RON: "lei", ZAR: "R", BRL: "R$",
  MXN: "$", MYR: "RM", AED: "د.إ", SAR: "SR",
  KWD: "KD", JOD: "د.ا", EGP: "E£", MAD: "DH",
  DZD: "دج", TND: "DT", ETB: "Br", UZS: "so'm",
};

/* ---------------------------- Core helpers ---------------------------- */

/** Returns the base USD price of a hit (un-discounted) */
export function getBaseUSD(hit) {
  return hit?.data?.priceUSD ?? hit?.data?.usd ?? hit?.usd ?? hit?.priceUSD ?? null;
}

function coercePercent(value) {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const parsed = coercePercent(entry);
      if (parsed) return parsed;
    }
    return 0;
  }
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const normalized = value.replace(/[^0-9.-]/g, "");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function getItemDiscountPercent(hit) {
  const pct = coercePercent(hit?.data?.discount ?? hit?.discount ?? null);
  if (!pct) return 0;
  return Math.min(100, Math.max(0, pct));
}

function applyItemDiscount(basePrice, discountPct) {
  if (typeof basePrice !== "number") return basePrice ?? null;
  if (!discountPct) return basePrice;
  const ratio = 1 - discountPct / 100;
  return Math.max(0, basePrice * ratio);
}

/** Returns the base price in the user’s currency (un-discounted) */
export function getPriceForCurrency(hit, currency) {
  const key = `price${String(currency || "USD").toUpperCase()}`;
  const basePrice = hit?.data?.[key] ?? hit?.[key] ?? null;
  const discountPct = getItemDiscountPercent(hit);
  return applyItemDiscount(basePrice, discountPct);
}

/** round up to 2 decimals (matches prior behavior using Math.ceil) */
function roundUp2(n) {
  return Math.max(0, Math.ceil((Number(n) || 0) * 100) / 100);
}

export function applyLocalDiscount(localBase, usdBase, pricing) {
  if (typeof localBase !== "number" || typeof usdBase !== "number") return null;

  try {
    // Preferred: use explain() if available
    if (pricing && typeof pricing.explain === "function") {
      const exp = pricing.explain(usdBase);
      const rolePct = Number(exp?.rolePct) || 0;
      const personalPct = Number(exp?.personalPct) || 0;
      const afterRole = localBase * (1 - rolePct / 100);
      const afterPersonal = afterRole * (1 - personalPct / 100);
      return roundUp2(afterPersonal);
    }

    // Fallback: ratio from USD computation
    if (pricing && typeof pricing.computePriceUSD === "function" && usdBase > 0) {
      const discountedUSD = pricing.computePriceUSD(usdBase);
      const ratio =
        typeof discountedUSD === "number" && discountedUSD >= 0
          ? discountedUSD / usdBase
          : 1;
      return roundUp2(localBase * ratio);
    }

    // No pricing info; return as-is
    return roundUp2(localBase);
  } catch {
    return roundUp2(localBase);
  }
}

/** Formats a numeric price into a consistent display string. */
export function formatPrice(value, currencyCode = "USD") {
  if (typeof value !== "number" || isNaN(value)) return "—";
  const symbol = SYMBOLS[currencyCode] || "";
  const base = value.toFixed(2);
  return symbol ? `${symbol} ${base}` : `${base} ${currencyCode}`;
}
